import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, ChevronLeft, ChevronRight, LayoutGrid, RefreshCw, AlertTriangle } from 'lucide-react';

// ─── Simulation Config ────────────────────────────────────────────────────────

const LIMIT = 3;       // max requests per window
const WINDOW_TICKS = 5; // window duration in ticks

// Each event has: type, optional label, optional note
const RAW_EVENTS = [
  { type: 'init' },
  // Window 1 (ticks 0-4)
  { type: 'request', label: 'GET /api/users',       tick: 1 },
  { type: 'request', label: 'POST /api/item',        tick: 2 },
  { type: 'request', label: 'GET /api/search',       tick: 3 },  // limit hit
  { type: 'request', label: 'DELETE /api/item/5',    tick: 3 },  // REJECTED
  { type: 'request', label: 'GET /api/profile',      tick: 4 },  // REJECTED
  // Window reset
  { type: 'window-reset', tick: 5 },
  // Window 2 (ticks 5-9)
  { type: 'request', label: 'GET /api/data',         tick: 5 },
  { type: 'request', label: 'GET /api/feed',         tick: 7 },
  // Window reset
  { type: 'window-reset', tick: 10 },
  // Window 3 — thundering herd demo
  // 3 requests near the END of the window
  { type: 'request', label: 'GET /api/burst-A',     tick: 13, note: 'near end of window' },
  { type: 'request', label: 'POST /api/burst-B',    tick: 13, note: 'near end of window' },
  { type: 'request', label: 'GET /api/burst-C',     tick: 14, note: 'near end of window — limit hit' },
  // Window reset
  { type: 'window-reset', tick: 15, note: 'thundering-herd' },
  // 3 more right at the START of the new window
  { type: 'request', label: 'GET /api/burst-D',     tick: 15, note: 'right at window start' },
  { type: 'request', label: 'POST /api/burst-E',    tick: 15, note: 'right at window start' },
  { type: 'request', label: 'GET /api/burst-F',     tick: 15, note: '6 requests in ~2 ticks — herd!' },
  // Window reset
  { type: 'window-reset', tick: 20 },
  { type: 'request', label: 'GET /api/normal',       tick: 21 },
];

// ─── Step Engine ──────────────────────────────────────────────────────────────

function buildSteps() {
  const steps = [];
  let windowIndex = 0;
  let windowStart = 0;
  let count = 0;
  let requestsInWindows = []; // array of { tick, label, result, windowIndex }
  let totalRequests = 0;
  let allowedCount = 0;
  let rejectedCount = 0;
  let windowResets = 0;

  for (const event of RAW_EVENTS) {
    if (event.type === 'init') {
      steps.push({
        id: 0,
        phase: 'init',
        count,
        windowStart,
        windowEnd: windowStart + WINDOW_TICKS,
        windowIndex,
        requests: [],
        totalRequests,
        allowedCount,
        rejectedCount,
        windowResets,
        currentTick: 0,
        currentEvent: null,
        title: 'Initialize Fixed Window Counter',
        badge: null,
        explanation:
          `Fixed Window Counter initialized. Time is divided into fixed windows of ${WINDOW_TICKS} ticks each. ` +
          `A counter tracks how many requests arrived in the current window. ` +
          `If the counter reaches the limit (${LIMIT}), all further requests in that window are rejected. ` +
          `When the window expires, the counter resets to 0 and a fresh window begins. ` +
          `This is the simplest rate limiting algorithm — O(1) memory per window, ` +
          `but it has a known weakness: a thundering herd at window boundaries.`,
      });
    } else if (event.type === 'request') {
      totalRequests++;
      const allowed = count < LIMIT;
      if (allowed) { count++; allowedCount++; } else { rejectedCount++; }

      const entry = {
        id: requestsInWindows.length,
        tick: event.tick,
        label: event.label,
        result: allowed ? 'allowed' : 'rejected',
        windowIndex,
        note: event.note || null,
      };
      requestsInWindows = [...requestsInWindows, entry];

      steps.push({
        id: steps.length,
        phase: allowed ? 'allowed' : 'rejected',
        count,
        windowStart,
        windowEnd: windowStart + WINDOW_TICKS,
        windowIndex,
        requests: [...requestsInWindows],
        totalRequests,
        allowedCount,
        rejectedCount,
        windowResets,
        currentTick: event.tick,
        currentEvent: entry,
        title: (allowed ? 'Allowed: ' : 'Rejected: ') + event.label,
        badge: allowed ? (count === LIMIT ? 'Limit Reached' : 'Allowed') : 'Rejected',
        explanation: allowed
          ? `Request "${event.label}" arrives at tick ${event.tick}. ` +
            `Counter: ${count - 1} → ${count}. Limit is ${LIMIT}. Request is ALLOWED. ` +
            (count === LIMIT
              ? `⚠ The counter is now at the limit (${LIMIT}/${LIMIT}). Any further requests in this window will be rejected until the window resets at tick ${windowStart + WINDOW_TICKS}.`
              : `${LIMIT - count} more request${LIMIT - count !== 1 ? 's' : ''} can be served in this window (expires at tick ${windowStart + WINDOW_TICKS}).`)
          : `Request "${event.label}" arrives at tick ${event.tick}. ` +
            `Counter is already at ${count}/${LIMIT} — the window is full. ` +
            `Request REJECTED (HTTP 429). ` +
            `The client must wait until the window resets at tick ${windowStart + WINDOW_TICKS} before retrying. ` +
            `Unlike Token Bucket, there's no partial recovery — the full window must expire first.`,
      });
    } else if (event.type === 'window-reset') {
      const prevCount = count;
      const prevWindowStart = windowStart;
      const prevWindowIndex = windowIndex;
      windowIndex++;
      windowStart = event.tick;
      count = 0;
      windowResets++;

      const isThunderingHerd = event.note === 'thundering-herd';

      steps.push({
        id: steps.length,
        phase: 'reset',
        count,
        windowStart,
        windowEnd: windowStart + WINDOW_TICKS,
        windowIndex,
        requests: [...requestsInWindows],
        totalRequests,
        allowedCount,
        rejectedCount,
        windowResets,
        currentTick: event.tick,
        currentEvent: null,
        isThunderingHerd,
        prevWindowStart,
        prevWindowIndex,
        prevCount,
        title: `Window Reset — Window #${windowIndex} begins`,
        badge: isThunderingHerd ? 'Thundering Herd Risk' : 'Window Reset',
        explanation: isThunderingHerd
          ? `Window ${prevWindowIndex + 1} expires. Counter reset from ${prevCount} to 0. ` +
            `Window ${windowIndex + 1} starts now. ` +
            `⚠ THUNDERING HERD: In the last 2 ticks, ${prevCount} requests hit the end of the previous window ` +
            `and ${LIMIT} more can immediately hit the start of this new window. ` +
            `That's up to ${prevCount + LIMIT} requests in a very short span — ${LIMIT * 2}× the intended rate. ` +
            `This is the core weakness of Fixed Window: the boundary creates a spike opportunity. ` +
            `Sliding Window algorithms solve this by not having a hard reset point.`
          : `Window ${prevWindowIndex + 1} (ticks ${prevWindowStart}–${prevWindowStart + WINDOW_TICKS}) has expired. ` +
            `It served ${prevCount} of ${LIMIT} allowed requests. ` +
            `Counter reset to 0. Window ${windowIndex + 1} starts at tick ${windowStart} ` +
            `and runs until tick ${windowStart + WINDOW_TICKS}. ` +
            `All ${LIMIT} request slots are available again.`,
      });
    }
  }

  return steps;
}

const STEPS = buildSteps();

// ─── Phase Styles ─────────────────────────────────────────────────────────────

const PHASE = {
  init:     { dot: 'bg-gray-400',    text: 'text-gray-600 dark:text-gray-400',         border: 'border-gray-200 dark:border-gray-700',     bg: 'bg-gray-50 dark:bg-gray-900/40',         badge: 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300 border-gray-200 dark:border-gray-700' },
  allowed:  { dot: 'bg-emerald-500', text: 'text-emerald-600 dark:text-emerald-400',   border: 'border-emerald-200 dark:border-emerald-800', bg: 'bg-emerald-50 dark:bg-emerald-950/40',   badge: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/60 dark:text-emerald-300 border-emerald-200 dark:border-emerald-800' },
  rejected: { dot: 'bg-red-500',     text: 'text-red-600 dark:text-red-400',           border: 'border-red-200 dark:border-red-800',         bg: 'bg-red-50 dark:bg-red-950/40',           badge: 'bg-red-100 text-red-700 dark:bg-red-900/60 dark:text-red-300 border-red-200 dark:border-red-800' },
  reset:    { dot: 'bg-violet-500',  text: 'text-violet-600 dark:text-violet-400',     border: 'border-violet-200 dark:border-violet-800',   bg: 'bg-violet-50 dark:bg-violet-950/40',     badge: 'bg-violet-100 text-violet-700 dark:bg-violet-900/60 dark:text-violet-300 border-violet-200 dark:border-violet-800' },
};

// ─── Window Counter Visual ────────────────────────────────────────────────────

function WindowCounterVisual({ step }) {
  const { count, windowStart, windowEnd, windowIndex, currentTick, isThunderingHerd } = step;
  const pct = (count / LIMIT) * 100;
  const barColor = count >= LIMIT ? 'bg-red-500' : count === LIMIT - 1 ? 'bg-amber-400' : 'bg-emerald-500';
  const windowProgress = currentTick != null
    ? Math.min(((currentTick - windowStart) / WINDOW_TICKS) * 100, 100)
    : 0;

  return (
    <div className="rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 overflow-hidden">
      <div className="flex items-center justify-between px-4 py-2.5 border-b border-gray-100 dark:border-gray-800 bg-gray-50 dark:bg-gray-800/60">
        <div className="flex items-center gap-2">
          <LayoutGrid className="w-3.5 h-3.5 text-violet-500" />
          <span className="text-xs font-bold text-gray-700 dark:text-gray-300 uppercase tracking-wide">Window Counter</span>
          <span className="text-xs text-gray-400 dark:text-gray-500">limit = {LIMIT} / window</span>
        </div>
        <span className="text-xs font-mono text-gray-500 tabular-nums">Window #{windowIndex + 1}</span>
      </div>

      <div className="p-5 space-y-5">
        {/* Counter display */}
        <div className="flex items-center gap-5">
          {/* Big counter */}
          <div className={`flex flex-col items-center justify-center w-24 h-24 rounded-2xl border-2 transition-all duration-300
            ${count >= LIMIT
              ? 'border-red-300 dark:border-red-700 bg-red-50 dark:bg-red-950/30'
              : count === LIMIT - 1
              ? 'border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-950/30'
              : 'border-emerald-300 dark:border-emerald-700 bg-emerald-50 dark:bg-emerald-950/30'}`}>
            <span className={`text-4xl font-bold tabular-nums transition-all duration-300
              ${count >= LIMIT ? 'text-red-600 dark:text-red-400' : count === LIMIT - 1 ? 'text-amber-600 dark:text-amber-400' : 'text-emerald-600 dark:text-emerald-400'}`}>
              {count}
            </span>
            <span className="text-xs text-gray-400 mt-0.5">/ {LIMIT}</span>
          </div>

          <div className="flex-1 space-y-3">
            {/* Request slots */}
            <div>
              <p className="text-[10px] text-gray-400 uppercase tracking-wide mb-1.5">Request Slots</p>
              <div className="flex gap-2">
                {Array.from({ length: LIMIT }, (_, i) => (
                  <div
                    key={i}
                    className={`flex-1 h-8 rounded-lg border-2 transition-all duration-500 flex items-center justify-center text-xs font-bold
                      ${i < count
                        ? count >= LIMIT
                          ? 'bg-red-100 dark:bg-red-900/30 border-red-300 dark:border-red-700 text-red-600 dark:text-red-400'
                          : 'bg-emerald-100 dark:bg-emerald-900/30 border-emerald-300 dark:border-emerald-700 text-emerald-600 dark:text-emerald-400'
                        : 'bg-gray-50 dark:bg-gray-800 border-dashed border-gray-300 dark:border-gray-600 text-gray-300 dark:text-gray-600'
                      }`}
                  >
                    {i < count ? '✓' : '·'}
                  </div>
                ))}
              </div>
            </div>

            {/* Capacity bar */}
            <div>
              <div className="flex justify-between text-[10px] text-gray-400 mb-1">
                <span>Counter</span>
                <span className={count >= LIMIT ? 'text-red-500 font-semibold' : ''}>{count} / {LIMIT} {count >= LIMIT ? '— FULL' : ''}</span>
              </div>
              <div className="h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                <div className={`h-full rounded-full transition-all duration-500 ${barColor}`} style={{ width: `${pct}%` }} />
              </div>
            </div>
          </div>
        </div>

        {/* Window timeline */}
        <div>
          <div className="flex justify-between text-[10px] text-gray-400 mb-1.5">
            <span className="uppercase tracking-wide">Window Progress</span>
            <span className="font-mono">tick {currentTick ?? windowStart} of {windowEnd - 1}</span>
          </div>
          <div className="relative h-5 bg-gray-100 dark:bg-gray-800 rounded-full overflow-hidden border border-gray-200 dark:border-gray-700">
            <div
              className="h-full rounded-full bg-violet-400 dark:bg-violet-600 transition-all duration-500"
              style={{ width: `${windowProgress}%` }}
            />
            <div className="absolute inset-0 flex items-center justify-center">
              <span className="text-[10px] font-mono text-gray-500 dark:text-gray-400 mix-blend-multiply dark:mix-blend-screen">
                t={windowStart} → t={windowEnd}
              </span>
            </div>
          </div>
        </div>

        {/* Thundering herd warning */}
        {isThunderingHerd && (
          <div className="flex items-start gap-2 p-3 rounded-lg bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800">
            <AlertTriangle className="w-4 h-4 text-amber-500 flex-shrink-0 mt-0.5" />
            <p className="text-xs text-amber-700 dark:text-amber-300">
              <strong>Thundering Herd:</strong> Up to {LIMIT * 2} requests can squeeze through in a 2-tick span at the window boundary — {LIMIT}× the normal rate.
            </p>
          </div>
        )}

        {/* Stats */}
        <div className="grid grid-cols-3 gap-3 pt-1 border-t border-gray-100 dark:border-gray-800">
          <div className="text-center p-2 rounded-lg bg-gray-50 dark:bg-gray-800/50">
            <p className="text-lg font-bold text-gray-700 dark:text-gray-200 tabular-nums">{step.totalRequests}</p>
            <p className="text-[10px] text-gray-400 uppercase tracking-wide">Requests</p>
          </div>
          <div className="text-center p-2 rounded-lg bg-emerald-50 dark:bg-emerald-950/20">
            <p className="text-lg font-bold text-emerald-600 dark:text-emerald-400 tabular-nums">{step.allowedCount}</p>
            <p className="text-[10px] text-emerald-500 uppercase tracking-wide">Allowed</p>
          </div>
          <div className="text-center p-2 rounded-lg bg-red-50 dark:bg-red-950/20">
            <p className="text-lg font-bold text-red-600 dark:text-red-400 tabular-nums">{step.rejectedCount}</p>
            <p className="text-[10px] text-red-500 uppercase tracking-wide">Rejected</p>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Request Timeline ─────────────────────────────────────────────────────────

function RequestTimeline({ step }) {
  const { requests, windowStart, windowEnd, windowIndex, currentTick, currentEvent, windowResets } = step;

  // Group requests by window
  const windows = [];
  for (let w = 0; w <= windowIndex; w++) {
    const wStart = w * WINDOW_TICKS;
    const wEnd = wStart + WINDOW_TICKS;
    const wRequests = requests.filter(r => r.windowIndex === w);
    windows.push({ index: w, start: wStart, end: wEnd, requests: wRequests, isCurrent: w === windowIndex });
  }

  return (
    <div className="rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 overflow-hidden">
      <div className="flex items-center justify-between px-4 py-2.5 border-b border-gray-100 dark:border-gray-800 bg-gray-50 dark:bg-gray-800/60">
        <div className="flex items-center gap-2">
          <RefreshCw className="w-3.5 h-3.5 text-violet-500" />
          <span className="text-xs font-bold text-gray-700 dark:text-gray-300 uppercase tracking-wide">Window Timeline</span>
        </div>
        <span className="text-xs text-gray-400 dark:text-gray-500 tabular-nums">{windowResets} reset{windowResets !== 1 ? 's' : ''} so far</span>
      </div>

      <div className="p-4 space-y-3 overflow-y-auto" style={{ maxHeight: '320px', scrollbarWidth: 'thin' }}>
        {windows.map((w) => (
          <div key={w.index} className={`rounded-lg border p-3 transition-all duration-300
            ${w.isCurrent
              ? 'border-violet-300 dark:border-violet-700 bg-violet-50/50 dark:bg-violet-950/20'
              : 'border-gray-100 dark:border-gray-800 bg-gray-50 dark:bg-gray-800/30'}`}>
            <div className="flex items-center justify-between mb-2">
              <span className={`text-[10px] font-bold uppercase tracking-wide ${w.isCurrent ? 'text-violet-600 dark:text-violet-400' : 'text-gray-400 dark:text-gray-500'}`}>
                Window #{w.index + 1} — t={w.start} to t={w.end - 1}
              </span>
              <span className={`text-[10px] tabular-nums font-mono ${w.isCurrent ? 'text-violet-500' : 'text-gray-400'}`}>
                {w.requests.filter(r => r.result === 'allowed').length} / {LIMIT}
              </span>
            </div>

            {w.requests.length === 0 ? (
              <p className="text-[10px] text-gray-300 dark:text-gray-600 italic">— no requests —</p>
            ) : (
              <div className="space-y-1">
                {w.requests.map(req => {
                  const isCurrent = currentEvent && req.id === currentEvent.id;
                  return (
                    <div
                      key={req.id}
                      className={`flex items-center gap-2 text-[11px] px-2 py-1 rounded transition-colors
                        ${isCurrent ? 'bg-blue-100 dark:bg-blue-950/50' : ''}`}
                    >
                      <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${req.result === 'allowed' ? 'bg-emerald-500' : 'bg-red-500'}`} />
                      <span className="font-mono text-gray-500 dark:text-gray-400 flex-shrink-0">t={req.tick}</span>
                      <span className={`font-mono truncate ${isCurrent ? 'text-blue-700 dark:text-blue-300 font-medium' : 'text-gray-600 dark:text-gray-400'}`}>
                        {req.label}
                      </span>
                      {req.result === 'allowed'
                        ? <span className="ml-auto flex-shrink-0 text-[10px] text-emerald-600 dark:text-emerald-400">✓</span>
                        : <span className="ml-auto flex-shrink-0 text-[10px] text-red-500 dark:text-red-400">429</span>}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Explanation Card ─────────────────────────────────────────────────────────

function ExplanationCard({ step }) {
  const c = PHASE[step.phase] || PHASE.init;
  return (
    <div className={`rounded-xl border ${c.border} ${c.bg} p-4`}>
      <div className="flex flex-wrap items-start gap-2 mb-2">
        <span className={`w-2 h-2 rounded-full mt-1.5 flex-shrink-0 ${c.dot}`} />
        <span className={`text-sm font-bold ${c.text}`}>{step.title}</span>
        {step.badge && (
          <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full border ${c.badge}`}>{step.badge}</span>
        )}
      </div>
      <p className="text-sm text-gray-600 dark:text-gray-300 leading-relaxed ml-4">{step.explanation}</p>
    </div>
  );
}

// ─── Step Sidebar ─────────────────────────────────────────────────────────────

function StepSidebar({ steps, currentIdx, onJump }) {
  const activeRef = useRef(null);
  useEffect(() => {
    activeRef.current?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  }, [currentIdx]);

  return (
    <div className="flex flex-col h-full overflow-hidden">
      <p className="text-[10px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-widest px-3 py-2 flex-shrink-0">Events</p>
      <div className="flex-1 overflow-y-auto space-y-0.5 px-2 pb-2" style={{ scrollbarWidth: 'thin' }}>
        {steps.map((s, i) => {
          const c = PHASE[s.phase] || PHASE.init;
          const isCurrent = i === currentIdx;
          const isPast = i < currentIdx;
          return (
            <button
              key={i}
              ref={isCurrent ? activeRef : null}
              onClick={() => onJump(i)}
              className={`w-full text-left px-2.5 py-2 rounded-lg flex items-center gap-2 text-[11px] transition-all duration-150
                ${isCurrent ? `${c.bg} border ${c.border} font-semibold`
                  : isPast ? 'hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-400 dark:text-gray-500'
                  : 'hover:bg-gray-50 dark:hover:bg-gray-800/50 text-gray-300 dark:text-gray-600'}`}
            >
              <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${isCurrent ? c.dot : isPast ? 'bg-gray-300 dark:bg-gray-600' : 'bg-gray-200 dark:bg-gray-700'}`} />
              <span className={`font-mono leading-tight truncate ${isCurrent ? c.text : ''}`}>
                <span className="text-gray-300 dark:text-gray-600 mr-1">{String(i + 1).padStart(2, '0')}.</span>
                {s.title}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────

export default function FixedWindowCounter() {
  const [idx, setIdx] = useState(0);
  const [animKey, setAnimKey] = useState(0);
  const total = STEPS.length;
  const step = STEPS[idx];

  const goTo = (i) => {
    setIdx(Math.max(0, Math.min(total - 1, i)));
    setAnimKey(k => k + 1);
  };

  useEffect(() => {
    const handler = (e) => {
      if (e.key === 'ArrowRight' || e.key === 'ArrowDown') { setIdx(i => Math.min(i + 1, total - 1)); setAnimKey(k => k + 1); }
      if (e.key === 'ArrowLeft'  || e.key === 'ArrowUp')   { setIdx(i => Math.max(i - 1, 0));          setAnimKey(k => k + 1); }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [total]);

  const c = PHASE[step.phase] || PHASE.init;

  return (
    <div className="h-screen flex flex-col bg-white dark:bg-gray-950 text-gray-900 dark:text-white overflow-hidden">

      {/* Top bar */}
      <div className="flex-shrink-0 border-b border-gray-200 dark:border-gray-800 bg-white/90 dark:bg-gray-950/90 backdrop-blur-sm z-10">
        <div className="max-w-screen-xl mx-auto px-4 py-3 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <Link to="/dev" className="flex items-center gap-1 text-sm text-gray-500 hover:text-blue-600 dark:hover:text-blue-400 transition-colors">
              <ArrowLeft className="w-4 h-4" />
              <span className="hidden sm:inline">Dev</span>
            </Link>
            <span className="text-gray-300 dark:text-gray-700">/</span>
            <h1 className="text-sm font-semibold text-gray-900 dark:text-white">Fixed Window Counter — Rate Limiting Simulator</h1>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-xs text-gray-400 tabular-nums">{idx + 1} / {total}</span>
            <div className="hidden sm:flex w-28 h-1.5 bg-gray-200 dark:bg-gray-800 rounded-full overflow-hidden">
              <div className="h-full rounded-full bg-gradient-to-r from-violet-500 to-emerald-500 transition-all duration-500" style={{ width: `${((idx + 1) / total) * 100}%` }} />
            </div>
            <span className="hidden sm:inline text-xs text-gray-400">← → keys</span>
          </div>
        </div>
      </div>

      {/* Body */}
      <div className="flex-1 flex overflow-hidden">
        <div className="hidden lg:flex flex-col w-52 xl:w-60 flex-shrink-0 border-r border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-900/50 overflow-hidden">
          <StepSidebar steps={STEPS} currentIdx={idx} onJump={goTo} />
        </div>

        <div className="flex-1 overflow-y-auto">
          <div key={animKey} className="max-w-4xl mx-auto px-4 py-5 space-y-5" style={{ animation: 'fadeSlideIn 0.25s ease' }}>
            <ExplanationCard step={step} />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <WindowCounterVisual step={step} />
              <RequestTimeline step={step} />
            </div>
            <div className="h-4" />
          </div>
        </div>
      </div>

      {/* Bottom nav */}
      <div className="flex-shrink-0 border-t border-gray-200 dark:border-gray-800 bg-white/95 dark:bg-gray-950/95 backdrop-blur-sm z-10">
        <div className="max-w-screen-xl mx-auto px-4 py-3 flex items-center justify-between gap-4">
          <button onClick={() => goTo(idx - 1)} disabled={idx === 0}
            className="flex items-center gap-1.5 px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-30 disabled:cursor-not-allowed transition-all">
            <ChevronLeft className="w-4 h-4" /> Prev
          </button>
          <div className="hidden lg:flex items-center gap-2 text-xs text-gray-400">
            {step.badge && <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium border ${c.badge}`}>{step.badge}</span>}
            <span className="font-mono">{step.title}</span>
          </div>
          <div className="flex lg:hidden items-center gap-2 text-xs text-gray-400">
            <div className="w-32 h-1.5 bg-gray-200 dark:bg-gray-800 rounded-full overflow-hidden">
              <div className="h-full rounded-full bg-gradient-to-r from-violet-500 to-emerald-500 transition-all duration-500" style={{ width: `${((idx + 1) / total) * 100}%` }} />
            </div>
            <span className="tabular-nums">{idx + 1}/{total}</span>
          </div>
          <button onClick={() => goTo(idx + 1)} disabled={idx === total - 1}
            className="flex items-center gap-1.5 px-4 py-2 rounded-lg border border-transparent bg-blue-600 hover:bg-blue-700 text-sm font-semibold text-white disabled:opacity-30 disabled:cursor-not-allowed transition-all shadow-sm">
            Next <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      </div>

      <style>{`@keyframes fadeSlideIn { from { opacity: 0; transform: translateY(6px); } to { opacity: 1; transform: translateY(0); } }`}</style>
    </div>
  );
}
